import type { ReactNode } from 'react';
import { Target, ArrowLeft, ArrowRight, Check, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface StepShellProps {
  stepNumber: number;
  totalSteps: number;
  title: string;
  goal: string;
  /** Main step content — inputs, measurements, tip cards, etc. */
  children: ReactNode;
  /** Per-step actions injected into the footer (Save & Exit, etc.). */
  extraFooterActions?: ReactNode;
  onBack: () => void;
  onNext: () => void;
  onSaveExit: () => void;
  canGoNext: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  isComplete: boolean;
}

/**
 * Shared chrome for every wizard step. Renders:
 *   - Step header (number, title, goal sentence with target icon)
 *   - Body (the step's interactive content)
 *   - Footer with Back / Save & Exit / Next
 *
 * Keeps every step visually consistent so users build muscle memory.
 */
export function StepShell({
  stepNumber, totalSteps, title, goal,
  children, extraFooterActions,
  onBack, onNext, onSaveExit, canGoNext,
  isFirstStep, isLastStep, isComplete,
}: StepShellProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-4 sm:px-8 pt-6 pb-4 border-b border-zinc-800/60">
        <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          Step {stepNumber} of {totalSteps}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">{title}</h1>
        <div className="flex items-start gap-2 mt-3 text-sm text-zinc-300">
          <Target size={16} className="text-ember-500 shrink-0 mt-0.5" />
          <p>{goal}</p>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 px-4 sm:px-8 py-6 overflow-y-auto space-y-5">
        {children}
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-800 px-4 sm:px-8 py-4 bg-zinc-950/80 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="md"
            leftIcon={<ArrowLeft size={14} />}
            disabled={isFirstStep}
            onClick={onBack}
          >
            Back
          </Button>

          <div className="flex items-center gap-2">
            {extraFooterActions}
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Save size={14} />}
              onClick={onSaveExit}
            >
              Save & exit
            </Button>
            <Button
              variant="primary"
              size="md"
              rightIcon={isLastStep ? <Check size={14} /> : <ArrowRight size={14} />}
              disabled={!canGoNext}
              onClick={onNext}
            >
              {isLastStep ? 'Finish & report' : isComplete ? 'Next' : 'Mark complete & next'}
            </Button>
          </div>
        </div>

        {/* v129 — Safety & Legal Notes link, persistent on every wizard step.
            Single edit here propagates to every step. Mobile: ≥44px tap target. */}
        <div className="mt-3 pt-3 border-t border-zinc-800/60 text-center">
          <a
            href="/pages/safety"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-h-[44px] px-3 text-[11px] font-mono text-zinc-500 hover:text-cyan-400 uppercase tracking-wider underline decoration-dotted"
          >
            Safety &amp; Legal Notes
          </a>
        </div>
      </footer>
    </div>
  );
}
